import { useRef, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'

import type { StationVoiceSetting } from '@/lib/api/station-voice-api'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
    useStationVoiceSettings,
    useUpdateStationVoiceSettings,
} from '@/hooks/use-station-voice-settings-query'
import { pickVietnameseVoice, renderSpeechTemplate } from '@/lib/station-tts'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/settings/station-voice',
)({ component: StationVoiceSettingsPage })

const CASE_LABEL: Record<string, () => string> = {
    new_order: m.station_voice_case_new_order,
    stock_out: m.station_voice_case_stock_out,
    in_delivery: m.station_voice_case_in_delivery,
    delivered: m.station_voice_case_delivered,
    delivery_failed: m.station_voice_case_delivery_failed,
    payment_received: m.station_voice_case_payment_received,
}

const PLACEHOLDERS: {
    token: string
    desc: () => string
    example: () => string
}[] = [
    {
        token: '{ma_don}',
        desc: m.station_voice_ph_ma_don_desc,
        example: m.station_voice_ph_ma_don_ex,
    },
    {
        token: '{ma_don_noi_bo}',
        desc: m.station_voice_ph_ma_don_noi_bo_desc,
        example: m.station_voice_ph_ma_don_noi_bo_ex,
    },
    {
        token: '{ten_khach}',
        desc: m.station_voice_ph_ten_khach_desc,
        example: m.station_voice_ph_ten_khach_ex,
    },
    {
        token: '{sdt}',
        desc: m.station_voice_ph_sdt_desc,
        example: m.station_voice_ph_sdt_ex,
    },
    {
        token: '{so_tien}',
        desc: m.station_voice_ph_so_tien_desc,
        example: m.station_voice_ph_so_tien_ex,
    },
    {
        token: '{nguoi_tao}',
        desc: m.station_voice_ph_nguoi_tao_desc,
        example: m.station_voice_ph_nguoi_tao_ex,
    },
    {
        token: '{nguoi_giao}',
        desc: m.station_voice_ph_nguoi_giao_desc,
        example: m.station_voice_ph_nguoi_giao_ex,
    },
]

const SAMPLE = {
    ma_don: 'DH123',
    ma_don_noi_bo: 'A45',
    ten_khach: 'Anh An',
    sdt: '0901234567',
    so_tien: 250_000,
    nguoi_tao: 'Anh Binh',
    nguoi_giao: 'Anh Cuong',
}

function speak(text: string) {
    if (!text || !('speechSynthesis' in window)) return
    const voice = pickVietnameseVoice()
    const u = new SpeechSynthesisUtterance(text)
    if (voice) {
        u.voice = voice
        u.lang = voice.lang
    }
    window.speechSynthesis.speak(u)
}

function StationVoiceSettingsPage() {
    const { data: rows = [], isLoading } = useStationVoiceSettings()
    const update = useUpdateStationVoiceSettings()
    const [draft, setDraft] = useState<StationVoiceSetting[] | null>(null)
    const items = draft ?? rows
    const refs = useRef<Record<string, HTMLTextAreaElement | null>>({})

    const setItem = (key: string, patch: Partial<StationVoiceSetting>) =>
        setDraft(
            items.map((it) =>
                it.event_key === key ? { ...it, ...patch } : it,
            ),
        )

    const insertToken = (key: string, token: string) => {
        const el = refs.current[key]
        const cur = items.find((i) => i.event_key === key)?.template ?? ''
        if (!el) {
            setItem(key, { template: cur + token })
            return
        }
        const start = el.selectionStart ?? cur.length
        const end = el.selectionEnd ?? cur.length
        setItem(key, { template: cur.slice(0, start) + token + cur.slice(end) })
    }

    const revert = (key: string) => {
        const server = rows.find((r) => r.event_key === key)
        if (server) {
            setItem(key, {
                template: server.template,
                enabled: server.enabled,
            })
        }
    }

    if (isLoading) {
        return (
            <div className="text-sm text-muted-foreground">
                {m.common_loading()}
            </div>
        )
    }

    return (
        <div className="max-w-3xl space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>{m.station_voice_title()}</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="mb-2 text-sm font-medium">
                        {m.station_voice_cheatsheet_title()}
                    </p>
                    <div className="overflow-x-auto rounded border">
                        <table className="w-full text-sm">
                            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                                <tr>
                                    <th className="px-3 py-1.5">Placeholder</th>
                                    <th className="px-3 py-1.5">
                                        {m.station_voice_col_reads()}
                                    </th>
                                    <th className="px-3 py-1.5">
                                        {m.station_voice_col_example()}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {PLACEHOLDERS.map((p) => (
                                    <tr key={p.token} className="border-t">
                                        <td className="px-3 py-1.5 font-mono">
                                            {p.token}
                                        </td>
                                        <td className="px-3 py-1.5">
                                            {p.desc()}
                                        </td>
                                        <td className="px-3 py-1.5 text-muted-foreground">
                                            {p.example()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>

            {items.map((it) => {
                const preview = renderSpeechTemplate(it.template, SAMPLE)
                return (
                    <Card key={it.event_key}>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-base">
                                    {CASE_LABEL[it.event_key]?.() ??
                                        it.event_key}
                                </CardTitle>
                                <Switch
                                    checked={it.enabled}
                                    onCheckedChange={(v) =>
                                        setItem(it.event_key, { enabled: v })
                                    }
                                />
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-2">
                            <div className="flex flex-wrap gap-1.5">
                                {PLACEHOLDERS.map((p) => (
                                    <button
                                        key={p.token}
                                        type="button"
                                        onClick={() =>
                                            insertToken(it.event_key, p.token)
                                        }
                                        className="rounded bg-muted px-2 py-0.5 font-mono text-xs hover:bg-muted/70"
                                    >
                                        {p.token}
                                    </button>
                                ))}
                            </div>
                            <Textarea
                                ref={(el) => {
                                    refs.current[it.event_key] = el
                                }}
                                rows={2}
                                value={it.template}
                                onChange={(e) =>
                                    setItem(it.event_key, {
                                        template: e.target.value,
                                    })
                                }
                            />
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-xs text-muted-foreground">
                                    {m.station_voice_preview_label()}:{' '}
                                    <span className="italic">
                                        {preview || '—'}
                                    </span>
                                </p>
                                <div className="flex shrink-0 gap-2">
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => speak(preview)}
                                    >
                                        {m.station_voice_test_btn()}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => revert(it.event_key)}
                                    >
                                        {m.station_voice_revert_btn()}
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )
            })}

            <Button
                disabled={update.isPending || !draft}
                onClick={() =>
                    draft &&
                    update.mutate(draft, { onSuccess: () => setDraft(null) })
                }
            >
                {m.station_voice_save_btn()}
            </Button>
        </div>
    )
}
